import { Component, Inject, OnDestroy, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SnackbarService } from 'src/app/services/snackbar.sevice';
import { DateTime } from 'luxon';
import { interval, Observable, Subscription } from 'rxjs';
import {
    AcknowledgeReferralRequest,
    Covid19Result,
    DecisionType,
    EntityType,
    GetDecision,
    GetDecisionItem,
    GetReferralDetails,
    GetReferralItem,
    PregnancyStatus,
    ReferralStatus,
    StrokeQueryType,
    UpsertNotification
} from 'src/app/api-module/control-centre-api-client';
import { ReferralsService } from '../referrals.service';
import {
    ReferralConstants,
    ReferralDetailsInjectData,
    statusesAllowingAcknowledgement,
    statusDisplayMapping,
    statusesAllowingDeferral
} from '../referrals.model';
import { catchError, map, startWith } from 'rxjs/operators';
import { ReferralsUtility } from '../referrals.utility';
import { v4 as uuidv4 } from 'uuid';
import { MessageBoxIcon, MessageBoxResult } from '@app/messagebox/messagebox.model';

@Component({
    selector: 'app-view-referral',
    templateUrl: './view-referral.component.html',
    styleUrls: ['./view-referral.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ViewReferralComponent implements OnInit, OnDestroy {
    public static readonly INTERVAL_TIME_SINCE_UPDATE = 30000;

    actionInProgress = false;
    private readonly referralId: number | undefined;
    private readonly incomingReferralDetails: GetReferralDetails | undefined;

    currentReferral: GetReferralDetails | undefined;
    referralWithChanges: GetReferralDetails | null = null;
    latestDecision: GetDecision | null | undefined;
    referralStatus = ReferralStatus;
    strokeQueryType = StrokeQueryType;
    decisionType = DecisionType;
    covid19Result = Covid19Result;
    ReferralsUtility = ReferralsUtility;
    statusDisplayMapping = statusDisplayMapping;
    timeSinceCall$!: Observable<string>;
    staticDecisionItemList: GetDecisionItem[] | null = null;
    staticReferralItemList: GetReferralItem[] | null = null;
    isDeferred: boolean;
    isDeferrable: boolean;

    private readonly hubSubscriptionId = uuidv4();
    private readonly _subscriptions = new Subscription();

    constructor(
        private readonly snackbarService: SnackbarService,
        private readonly referralsService: ReferralsService,
        @Optional() public dialogRef: MatDialogRef<ViewReferralComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: ReferralDetailsInjectData
    ) {
        if (data) {
            this.referralId = data.referralId;
            this.incomingReferralDetails = data.referralDetails;
        }
    }

    get isStroke(): boolean {
        return this.currentReferral ? this.currentReferral.strokeQueryType !== StrokeQueryType.None : false;
    }

    get hasDeclinedReason(): boolean {
        return (
            this.currentReferral?.status === ReferralStatus.Declined &&
            this.latestDecision?.decisionType === DecisionType.Declined &&
            !!this.latestDecision?.reason
        );
    }

    get hasRefusalReason(): boolean {
        return this.currentReferral?.status === ReferralStatus.Refused;
    }

    async ngOnInit(): Promise<void> {
        await this.initReferral();
    }

    async ngOnDestroy(): Promise<void> {
        this._subscriptions.unsubscribe();

        await this.removeHubSubscription();
    }

    private async initReferral(): Promise<void> {
        if (this.incomingReferralDetails) {
            this.setReferralDetails(this.incomingReferralDetails);
            await this.initHubSubscription(this.incomingReferralDetails);
        } else {
            this._subscriptions.add(
                this.referralsService
                    .getReferral(this.referralId)
                    .pipe(catchError(ReferralsUtility.getErrorMessage))
                    .subscribe({
                        next: async (referral: GetReferralDetails) => {
                            this.setReferralDetails(referral);
                            await this.initHubSubscription(referral);
                        },
                        error: e => {
                            this.snackbarService.openSnackBar(e, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        }
                    })
            );
        }
    }

    private setReferralDetails(referral: GetReferralDetails) {
        // Set the deferrable/deferred properties before we force update the status
        this.isDeferred = referral?.status === ReferralStatus.Deferred;
        this.isDeferrable = referral ? statusesAllowingDeferral.includes(referral.status) : false;

        referral = ReferralsUtility.transformToStatusAwaitingAcknowledgement(referral);
        this.referralWithChanges = null;
        this.currentReferral = referral;
        this.latestDecision = referral ? ReferralsUtility.getLatestDecision(referral.decisions) : null;
        this.setScanRegionList(this.latestDecision);
        this.initTimeSinceCall(referral);
    }

    initTimeSinceCall(referral: GetReferralDetails): void {
        this.timeSinceCall$ = interval(ViewReferralComponent.INTERVAL_TIME_SINCE_UPDATE).pipe(
            startWith(0),
            map(() => {
                if (!referral) {
                    return '';
                }
                const sourceDateInstance = DateTime.fromISO(referral.processInitiatedAt);
                return DateTime.now().diff(sourceDateInstance).toFormat('hh:mm');
            })
        );
    }

    setScanRegionList(latestDecision: GetDecision | null): void {
        if (latestDecision?.decisionItems.length) {
            this.staticDecisionItemList = latestDecision.decisionItems ?? [];
            this.staticReferralItemList = null;
        } else {
            this.staticReferralItemList = this.currentReferral?.referralItems ?? [];
            this.staticDecisionItemList = null;
        }
    }

    async initHubSubscription(referral: GetReferralDetails) {
        try {
            await this.referralsService.subscribeToReferralUpdates(
                this.hubSubscriptionId,
                referral.orgCode,
                [referral.siteId],
                upsertNotification => this.onEntityUpsert(upsertNotification),
                (entityId, entityType) => this.onEntityDelete(entityId, entityType)
            );
        } catch (error) {
            console.warn(error);
        }
    }

    async removeHubSubscription() {
        await this.referralsService.unsubscribeFromReferralUpdates(this.hubSubscriptionId);
    }

    onEntityUpsert(upsertNotification: UpsertNotification) {
        if (upsertNotification.baseEntityType === EntityType.Referral) {
            const updatedReferral = upsertNotification.updatedEntityDetails as GetReferralDetails;
            if (updatedReferral.id === this.referralId) {
                const isMoreRecent =
                    !this.referralWithChanges ||
                    DateTime.fromISO(updatedReferral.updatedAt) >= DateTime.fromISO(this.referralWithChanges.updatedAt);
                if (isMoreRecent) {
                    this.referralWithChanges = ReferralsUtility.transformToStatusAwaitingAcknowledgement(updatedReferral);
                }
            }
        }
    }

    onEntityDelete(entityId: string, entityType: EntityType) {
        if (entityType === EntityType.Referral && entityId === String(this.currentReferral?.id)) {
            this.snackbarService.openSnackBar(
                'The site of this referral has changed. It may not be accessible anymore.',
                SnackbarService.SNACK_BAR_CLASS_ALERT,
                1000 * 30 // Show this for 30 seconds, important user sees it
            );
            this.dialogRef.close();
        }
    }

    hasPendingChanges(): boolean {
        if (this.currentReferral && this.referralWithChanges) {
            return DateTime.fromISO(this.currentReferral.updatedAt) <= DateTime.fromISO(this.referralWithChanges.updatedAt);
        }
        return false;
    }

    loadPendingChanges(): void {
        if (this.referralWithChanges) {
            // Update details of more recent referral
            this.setReferralDetails(this.referralWithChanges);
        }
    }

    closeForm(isSaved = false): void {
        this.dialogRef.close(isSaved);
    }

    displayDate(dateTime: string, showTime: boolean): string {
        return dateTime ? ReferralsUtility.DisplayDateTime(dateTime, showTime) : '';
    }

    getClinicalNotesForDisplay(): string {
        return [this.currentReferral?.clinicalHistory, this.latestDecision?.clinicalNotes].join('\n');
    }

    // If the updatedBy field is all lower case and contains no spaces, it is their username (e.g. 'jmacalpine')
    // And requires conversion. If it does not, it is their full name and can be displayed as is.
    displayDecisionBy(updatedBy: string): string {
        if (this.isFullName(updatedBy)) {
            return 'Doctor ' + updatedBy;
        } else {
            return updatedBy ? `Doctor ${ReferralsUtility.capitalizeFirstLetter(updatedBy.substring(1))}` : '';
        }
    }

    displayPregnancyStatus(pregnancyStatus: PregnancyStatus): string {
        return pregnancyStatus === PregnancyStatus.YesScanConsent ? 'Yes (scan consented)' : pregnancyStatus;
    }

    isReferralAcknowledgable(): boolean {
        return this.currentReferral ? statusesAllowingAcknowledgement.includes(this.currentReferral.status) : false;
    }

    async acknowledge(): Promise<void> {
        const request = new AcknowledgeReferralRequest({
            rowVersion: this.currentReferral.rowVersion,
            willAcknowledgeReferral: true
        });
        this.actionInProgress = true;
        await this.actionForReferral(this.currentReferral.id, request);
        this.actionInProgress = false;
    }

    async defer(): Promise<void> {
        this.referralsService
            .openViewReferralConfirmationPopup({
                title: 'Defer Referral Confirmation',
                icon: MessageBoxIcon.WarningTriangle,
                message:
                    ' Please note that deferring a referral is final and cannot be undone. Confirm below to defer this referral request.',
                showInput: true
            })
            .subscribe(async deferralConfirmation => {
                if (deferralConfirmation.result === MessageBoxResult.Yes) {
                    const request = new AcknowledgeReferralRequest({
                        rowVersion: this.currentReferral.rowVersion,
                        reasonForDeferral: deferralConfirmation.body,
                        willAcknowledgeReferral: false
                    });
                    this.actionInProgress = true;
                    await this.actionForReferral(this.currentReferral.id, request);
                    this.actionInProgress = false;
                }
            });
    }

    async actionForReferral(id: number, request: AcknowledgeReferralRequest): Promise<void> {
        try {
            await this.referralsService.acknowledgeReferral(id, request).toPromise();

            this.snackbarService.openSnackBar(ReferralConstants.ACTIONED_SUCCESS_MESSAGE, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
            this.dialogRef.close(true);
        } catch (err) {
            const errMessage = err instanceof Error ? err.message : ReferralConstants.CANNOT_ACTIONED_ERROR_MESSAGE;
            this.snackbarService.openSnackBar(errMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
        }
    }

    isAcknowledged(): boolean {
        return this.currentReferral?.status === ReferralStatus.Acknowledged;
    }

    private isFullName(updatedBy: string): boolean {
        return updatedBy && (updatedBy.toLocaleLowerCase() !== updatedBy || updatedBy.includes(' '));
    }
}
